import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import type { OrgRole } from '../../org-permissions/types/permissions.types';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (email: string, firstName: string, lastName: string, role: OrgRole) => Promise<void>;
  maxUsers: number;
  currentMemberCount: number;
}

export const InviteMemberDialog = ({
  open,
  onOpenChange,
  onConfirm,
  maxUsers,
  currentMemberCount,
}: InviteMemberDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<OrgRole>('org_member');
  const [isInviting, setIsInviting] = useState(false);

  const remainingSeats = maxUsers - currentMemberCount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      toast({ variant: 'destructive', title: t('ORGTEAM_INVITEDIALOG_ERRORS_NAMEREQUIRED') });
      return;
    }

    if (!email || !email.includes('@')) {
      toast({ variant: 'destructive', title: t('ORGTEAM_INVITEDIALOG_ERRORS_INVALIDEMAIL') });
      return;
    }

    if (remainingSeats <= 0) {
      toast({ variant: 'destructive', title: t('ORGTEAM_INVITEDIALOG_ERRORS_LIMITREACHED') });
      return;
    }

    setIsInviting(true);
    try {
      await onConfirm(email, firstName.trim(), lastName.trim(), role);
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('org_member');
      onOpenChange(false);
    } catch {
      // Error handling is done in parent component
    } finally {
      setIsInviting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isInviting) {
      if (!newOpen) {
        setEmail('');
        setFirstName('');
        setLastName('');
        setRole('org_member');
      }
      onOpenChange(newOpen);
    }
  };

  const getRoleDescription = (selectedRole: OrgRole) => {
    switch (selectedRole) {
      case 'org_admin':
        return t('ORGTEAM_INVITEDIALOG_ROLES_ADMINDESCRIPTION');
      case 'org_editor':
        return t('ORGTEAM_INVITEDIALOG_ROLES_EDITORDESCRIPTION');
      case 'org_member':
        return t('ORGTEAM_INVITEDIALOG_ROLES_MEMBERDESCRIPTION');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white w-[95vw] max-w-md max-h-[90dvh] p-4 sm:p-6 rounded-2xl flex flex-col gap-0 overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 min-h-0 flex-1 overflow-hidden">
          <DialogHeader className="pr-8 text-left">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-selise-blue/10">
                <UserPlus className="size-5 text-selise-blue" />
              </div>
              <DialogTitle>{t('ORGTEAM_INVITEDIALOG_TITLE')}</DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              {t('ORGTEAM_INVITEDIALOG_DESCRIPTION')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mr-2 py-1 pl-1 pr-3">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('ORGTEAM_INVITEDIALOG_FIELDS_FIRSTNAMELABEL')}</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={t('ORGTEAM_INVITEDIALOG_FIELDS_FIRSTNAMEPLACEHOLDER')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isInviting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('ORGTEAM_INVITEDIALOG_FIELDS_LASTNAMELABEL')}</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder={t('ORGTEAM_INVITEDIALOG_FIELDS_LASTNAMEPLACEHOLDER')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isInviting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('ORGTEAM_INVITEDIALOG_FIELDS_EMAILLABEL')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('ORGTEAM_INVITEDIALOG_FIELDS_EMAILPLACEHOLDER')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  disabled={isInviting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t('ORGTEAM_INVITEDIALOG_FIELDS_ROLELABEL')}</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as OrgRole)}
                disabled={isInviting}
              >
                <SelectTrigger id="role" className="border-secondary-100 h-11!">
                  <SelectValue placeholder={t('ORGTEAM_INVITEDIALOG_FIELDS_ROLEPLACEHOLDER')} />
                </SelectTrigger>
                <SelectContent className="z-99999">
                  <SelectItem value="org_admin">
                    <div className="flex items-center gap-2">
                      <span>{t('ORGTEAM_ROLES_ADMIN')}</span>
                      <span className="text-xs text-globe-grey">
                        {t('ORGTEAM_INVITEDIALOG_ROLES_ADMINSHORT')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="org_editor">
                    <div className="flex items-center gap-2">
                      <span>{t('ORGTEAM_ROLES_EDITOR')}</span>
                      <span className="text-xs text-globe-grey">
                        {t('ORGTEAM_INVITEDIALOG_ROLES_EDITORSHORT')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="org_member">
                    <div className="flex items-center gap-2">
                      <span>{t('ORGTEAM_ROLES_MEMBER')}</span>
                      <span className="text-xs text-globe-grey">
                        {t('ORGTEAM_INVITEDIALOG_ROLES_MEMBERSHORT')}
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-globe-grey">{getRoleDescription(role)}</p>
            </div>

            {remainingSeats <= 3 && remainingSeats > 0 && (
              <p className="text-xs text-oxford-blue">
                {t('ORGTEAM_INVITEDIALOG_SEATSREMAINING', {
                  count: remainingSeats,
                })}
              </p>
            )}
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleOpenChange(false)}
              disabled={isInviting}
            >
              {t('ORGTEAM_INVITEDIALOG_ACTIONS_CANCEL')}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                isInviting || !email || !firstName.trim() || !lastName.trim() || remainingSeats <= 0
              }
            >
              {isInviting && <Loader2 className="size-4 animate-spin mr-2" />}
              {t('ORGTEAM_INVITEDIALOG_ACTIONS_SEND')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
